// src/components/SideLists.jsx
import React from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Divider,
  VStack,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import { FaSun, FaMoon, FaMapMarkerAlt } from "react-icons/fa";

/**
 * Sección genérica para cada bloque (Provincias, Cantones, Parroquias)
 * dentro de un card de Diurno o Nocturno.
 */
const SectionList = ({ title, dataArray, colors }) => {
  // Ajustar la altura máxima según el tipo de sección
  const getMaxHeight = () => {
    switch (title.toLowerCase()) {
      case "provincias":
        return "100px";
      case "cantones":
        return "150px";
      case "parroquias":
        return "250px";
      default:
        return "100px";
    }
  };

  return (
    <Box mb={4}>
      <Heading size="sm" mb={2} display="flex" alignItems="center" gap={2}>
        <Icon as={FaMapMarkerAlt} color={colors.textMuted} />
        {title} ({dataArray.length})
      </Heading>
      <Divider mb={2} borderColor={colors.border} />

      <VStack
        align="stretch"
        spacing={1}
        maxH={getMaxHeight()}
        overflowY="auto"
        sx={{
          "&::-webkit-scrollbar": {
            width: "8px",
            borderRadius: "8px",
            backgroundColor: colors.hover,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.border,
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        {dataArray.map((item, idx) => (
          <Box
            key={idx}
            display="flex"
            justifyContent="space-between"
            bg={colors.hover}
            borderRadius="md"
            p={2}
            transition="all 0.2s ease"
            _hover={{ bg: colors.headerBg }}
          >
            <Text
              fontWeight="semibold"
              color={colors.text}
              fontSize="sm"
              noOfLines={2}
            >
              {item.level}
            </Text>
            <Text color={colors.primary} fontSize="sm" ml={2} flexShrink={0}>
              {item.total_vehiculos.toLocaleString()}
            </Text>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

/**
 * Card para Diurno o Nocturno, recibiendo las listas
 * de provincias, cantones y parroquias.
 */
const DayNightCard = ({
  title,
  icon,
  bgHeader,
  textColorHeader = "white",
  provinceData = [],
  cantonData = [],
  parroquiaData = [],
  colors,
}) => {
  return (
    <Card
      borderRadius="md"
      overflow="hidden"
      bg={colors.cardBg}
      borderColor={colors.border}
      borderWidth="1px"
      boxShadow={colors.shadow}
    >
      <CardHeader bg={bgHeader} color={textColorHeader} py={2}>
        <Heading size="md" display="flex" alignItems="center" gap={2}>
          <Icon as={icon} />
          {title}
        </Heading>
      </CardHeader>
      <CardBody>
        <SectionList
          title="Provincias"
          dataArray={provinceData}
          colors={colors}
        />
        <SectionList title="Cantones" dataArray={cantonData} colors={colors} />
        <SectionList
          title="Parroquias"
          dataArray={parroquiaData}
          colors={colors}
        />
      </CardBody>
    </Card>
  );
};

/**
 * Renderiza dos tarjetas: Diurno y Nocturno,
 * cada una con Provincias, Cantones y Parroquias.
 */
const SideLists = ({
  dayProvince = [],
  dayCanton = [],
  dayParroquia = [],
  nightProvince = [],
  nightCanton = [],
  nightParroquia = [],
  colors,
}) => {
  return (
    <SimpleGrid columns={2} gap={4} minW="320px" maxW="400px">
      {/* Tarjeta de Diurno */}
      <DayNightCard
        title="Diurno"
        icon={FaSun}
        bgHeader={colors.primary}
        provinceData={dayProvince}
        cantonData={dayCanton}
        parroquiaData={dayParroquia}
        colors={colors}
      />

      {/* Tarjeta de Nocturno */}
      <DayNightCard
        title="Nocturno"
        icon={FaMoon}
        bgHeader={colors.secondary}
        provinceData={nightProvince}
        cantonData={nightCanton}
        parroquiaData={nightParroquia}
        colors={colors}
      />
    </SimpleGrid>
  );
};

export default SideLists;
